import { ChevronRight, LogIn, Trash2 } from 'lucide-react'
import { MouseEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'

import { RoleBadge } from '@/containers/AdminUsers/RoleBadge'
import { useAdminUserImpersonation } from '@/hooks/useAdminUserImpersonation'
import API from '@/lib/api'
import { AdminUser } from '@/types/User'

import { ConfirmButton } from '../ConfirmButton'
import { Button } from '../ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip'

const stop = (e: MouseEvent) => e.stopPropagation()

export const AdminUsersTable = ({
  users,
  refresh,
}: {
  users: AdminUser[]
  refresh: () => void
}) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'admin.users',
  })
  const navigate = useNavigate()
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
  const { impersonate, impersonatingUserId } = useAdminUserImpersonation()

  const onDeleteUser = (userId: number) => {
    API.delete(`/api/v2/users/${userId}`, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
      .then(() => {
        toast.success(t('delete.success'))
        setSelectedUserId(null)
        refresh()
      })
      .catch(() => {
        toast.error(t('delete.error'))
      })
  }

  return (
    <Table>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead>Uživatel</TableHead>
          <TableHead className="hidden md:table-cell">Účty a role</TableHead>
          <TableHead className="hidden w-28 md:table-cell">Stav</TableHead>
          <TableHead className="w-px md:w-32" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {users.map((user) => (
          <TableRow key={user.id} onClick={() => navigate(`/admin/users/${user.id}`)} className="group cursor-pointer">
            <TableCell className="w-full max-w-0 md:w-auto">
              <div className="flex items-center gap-2">
                <p className="truncate font-medium">{user.fullName || user.email}</p>
                {user.roles.includes('admin') && <RoleBadge>{t('roles.admin')}</RoleBadge>}
              </div>
              {user.fullName && <p className="truncate text-xs text-muted-foreground">{user.email}</p>}
              <p className="truncate text-xs text-muted-foreground md:hidden">
                {[
                  !user.verified ? t('list.state.unverified') : null,
                  user.accounts.map((account) => account.accountName || account.accountId).join(', '),
                ]
                  .filter(Boolean)
                  .join(', ')}
              </p>
            </TableCell>
            <TableCell className="hidden max-w-0 md:table-cell">
              <div className="flex flex-col gap-1">
                {user.accounts.length === 0 && <span className="text-muted-foreground">-</span>}
                {user.accounts.map((account) => (
                  <div key={account.id} className="flex min-w-0 items-center gap-2">
                    <span className="truncate">{account.accountName || account.accountId}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {account.roles
                        .filter((role) => role !== 'admin')
                        .map((role) => t(`roles.${role}`))
                        .join(', ')}
                    </span>
                  </div>
                ))}
              </div>
            </TableCell>
            <TableCell className="hidden md:table-cell">
              {user.verified ? (
                <span className="text-muted-foreground">{t('detail.profile.verified')}</span>
              ) : (
                <RoleBadge muted>{t('list.state.unverified')}</RoleBadge>
              )}
            </TableCell>
            <TableCell onClick={stop}>
              <div className="flex items-center justify-end gap-0.5">
                <div className="flex items-center gap-0.5 transition-opacity md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={t('list.actions.impersonate')}
                        disabled={impersonatingUserId === user.id}
                        onClick={() => impersonate(user.id)}
                      >
                        <LogIn />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t('list.actions.impersonate')}</TooltipContent>
                  </Tooltip>
                  <ConfirmButton
                    open={selectedUserId === user.id}
                    onOpenChange={(open: boolean) => {
                      if (!open) {
                        setSelectedUserId(null)
                      }
                    }}
                    title={t('delete.action') + ' ' + (user.fullName ?? user.email)}
                    description={t('delete.description')}
                    onConfirm={() => {
                      onDeleteUser(user.id)
                    }}
                  >
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={t('delete.action')}
                      className="hover:text-danger"
                      onClick={() => setSelectedUserId(user.id)}
                    >
                      <Trash2 />
                    </Button>
                  </ConfirmButton>
                </div>
                <ChevronRight className="hidden size-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 md:block" />
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
